import type {
  PrototyperBuildTimelineV2,
  PrototyperSegmentKind,
  PrototyperTimelineItem,
  PrototyperTimelineSegment,
} from '../types/conversation.types';

/**
 * What a prototype build is doing, in the founder's words. The chat timeline and the preview's
 * progress card both read this, so they always say the same thing.
 *
 * The stored timeline keeps its engineer wording ("Patch ShiftHandoverBoardPage.tsx",
 * "Mode: webapp"); it is translated here when shown, so timelines saved before this change read
 * the same way as new ones.
 */

/** The four steps of the preview's progress card. */
export type BuildStage = 'plan' | 'change' | 'check' | 'ready';

export const BUILD_STAGES: ReadonlyArray<{ stage: BuildStage; label: string }> = [
  { stage: 'plan', label: 'Plan' },
  { stage: 'change', label: 'Change' },
  { stage: 'check', label: 'Check' },
  { stage: 'ready', label: 'Ready' },
];

export const FRIENDLY_SEGMENT_LABEL: Record<PrototyperSegmentKind, string> = {
  planning: 'Planning the change',
  exploring: 'Reading your prototype',
  writing_files: 'Making changes',
  build: 'Checking it works',
  fixing: 'Fixing a problem',
  finalizing: 'Finishing up',
  done: 'Ready',
};

const SEGMENT_STAGE: Record<PrototyperSegmentKind, BuildStage> = {
  planning: 'plan',
  exploring: 'plan',
  writing_files: 'change',
  build: 'check',
  fixing: 'change',
  finalizing: 'ready',
  done: 'ready',
};

export type FriendlyItemKind =
  | 'read'
  | 'search'
  | 'created'
  | 'updated'
  | 'removed'
  | 'check'
  | 'other';

export interface FriendlyItem {
  kind: FriendlyItemKind;
  /** "Updated ShiftHandoverBoardPage", "Read TopNav", "Checking it works". */
  title: string;
  /** While running: "Updating ShiftHandoverBoardPage". */
  action: string;
  /** The file's name without folders or extension, when the item is about one file. */
  fileLabel?: string;
}

/** `src/pages/ShiftHandoverBoardPage.tsx` → `ShiftHandoverBoardPage`. */
export function friendlyFileLabel(path: string): string {
  const name = path.split(/[\\/]/).pop() ?? path;
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(0, dot) : name;
}

/** The file named in a stored item title ("Patch App.tsx", "Read src/App.tsx"), if any. */
function fileOfTitle(title: string, verb: string): string | undefined {
  const rest = title.slice(verb.length).trim();
  return rest ? friendlyFileLabel(rest) : undefined;
}

/** One stored timeline item in the founder's words. */
export function friendlyItem(item: PrototyperTimelineItem): FriendlyItem {
  const title = item.title ?? '';
  const tool = item.toolName;
  if (tool === 'readFile' || title.startsWith('Read ')) {
    const file = fileOfTitle(title, 'Read');
    return {
      kind: 'read',
      title: file ? `Read ${file}` : 'Read a file',
      action: file ? `Reading ${file}` : 'Reading your prototype',
      fileLabel: file,
    };
  }
  if (tool === 'searchFiles' || title.startsWith('Search ')) {
    return { kind: 'search', title: 'Searched the code', action: 'Searching the code' };
  }
  if (tool === 'writeFile' || title.startsWith('Write ')) {
    const file = fileOfTitle(title, 'Write');
    return {
      kind: 'created',
      title: file ? `Wrote ${file}` : 'Wrote a file',
      action: file ? `Writing ${file}` : 'Writing a file',
      fileLabel: file,
    };
  }
  if (tool === 'searchReplace' || title.startsWith('Patch ')) {
    const file = fileOfTitle(title, 'Patch');
    return {
      kind: 'updated',
      title: file ? `Updated ${file}` : 'Updated a file',
      action: file ? `Updating ${file}` : 'Updating a file',
      fileLabel: file,
    };
  }
  if (tool === 'deleteFile' || title.startsWith('Delete ')) {
    const file = fileOfTitle(title, 'Delete');
    return {
      kind: 'removed',
      title: file ? `Removed ${file}` : 'Removed a file',
      action: file ? `Removing ${file}` : 'Removing a file',
      fileLabel: file,
    };
  }
  if (tool === 'build') {
    const title =
      item.status === 'error' ? 'Found a problem' : item.status === 'done' ? 'Passed' : 'Checking';
    return { kind: 'check', title, action: 'Checking it works' };
  }
  if (tool === 'configureSession') {
    return { kind: 'other', title: 'Set up the build', action: 'Setting up the build' };
  }
  return { kind: 'other', title, action: title };
}

/** Engineer wording a stored planning body carries ("Mode: webapp · 2 planned file(s)"). */
export function isTechnicalPlanningBody(body: string | undefined): boolean {
  if (!body) return false;
  return /(^|·\s*)(Mode|Language|Skills):/.test(body) || /planned file\(s\)/.test(body);
}

/** The planning stage's sentence: the edit's intent, else how many files it will touch. */
export function planningSentence(segment: PrototyperTimelineSegment): string | undefined {
  if (segment.intent?.trim()) return segment.intent.trim();
  if (segment.body && !isTechnicalPlanningBody(segment.body)) return segment.body;
  const files = (segment.items ?? []).filter((i) => i.key.startsWith('plan-file-')).length;
  if (files > 0) return files === 1 ? 'One file to change' : `${files} files to change`;
  return undefined;
}

export interface BuildActivity {
  stage: BuildStage;
  /** One line for right now: "Updating ShiftHandoverBoardPage", "Checking it works". */
  action: string;
  /** When the running stage started, for the elapsed time. */
  startedAt: string | null;
  /** A build failed and the model is repairing it. */
  fixing: boolean;
}

function runningSegment(
  segments: PrototyperTimelineSegment[]
): PrototyperTimelineSegment | undefined {
  for (let i = segments.length - 1; i >= 0; i--) {
    if (segments[i]!.status === 'running') return segments[i];
  }
  return undefined;
}

/**
 * What a live build is doing now, or null before its first event. The running stage decides;
 * between stages (one closed, the next not open yet) the last stage decides.
 */
export function describeBuildActivity(
  timeline: PrototyperBuildTimelineV2 | null | undefined
): BuildActivity | null {
  const segments = timeline?.segments ?? [];
  if (segments.length === 0) return null;
  const running = runningSegment(segments);
  const segment = running ?? segments[segments.length - 1]!;
  const stage = SEGMENT_STAGE[segment.kind] ?? 'plan';
  const fixing = segment.kind === 'fixing';
  const startedAt = segment.startedAt ?? null;

  const items = segment.items ?? [];
  const runningItem = [...items].reverse().find((i) => i.status === 'running');
  const lastItem = items[items.length - 1];

  let action: string;
  switch (segment.kind) {
    case 'planning':
      action = 'Planning the change';
      break;
    case 'exploring': {
      const item = runningItem ?? lastItem;
      action = item ? friendlyItem(item).action : 'Reading your prototype';
      break;
    }
    case 'writing_files': {
      const item = runningItem ?? lastItem;
      action = item ? friendlyItem(item).action : 'Making changes';
      break;
    }
    case 'build':
      action = 'Checking it works';
      break;
    case 'fixing':
      action = 'Fixing a problem the check found';
      break;
    default:
      action = running ? FRIENDLY_SEGMENT_LABEL[segment.kind] : 'Opening the new version';
  }
  return { stage, action, startedAt, fixing };
}

export interface BuildSummary {
  /** Files created, updated or removed, counted once each. */
  filesChanged: number;
  filesRead: number;
}

export function summarizeBuildTimeline(
  timeline: PrototyperBuildTimelineV2 | null | undefined
): BuildSummary {
  const changed = new Set<string>();
  const read = new Set<string>();
  for (const segment of timeline?.segments ?? []) {
    for (const item of segment.items ?? []) {
      const f = friendlyItem(item);
      const key = f.fileLabel ?? item.key;
      if (f.kind === 'created' || f.kind === 'updated' || f.kind === 'removed') changed.add(key);
      else if (f.kind === 'read') read.add(key);
    }
  }
  return { filesChanged: changed.size, filesRead: read.size };
}
